/* Anam Bijoux — mouvement de la charte (anam-wow.css, anam-sections.css).
   - .anam-vu ajoutée aux éléments quand ils entrent à l'écran ;
   - titres [data-anam-mots] découpés en mots pour une révélation en cascade ;
   - parallaxe douce des visuels [data-anam-parallaxe="n"] (n = amplitude en %) ;
   - barre de progression de lecture et curseur « Voir » sur ordinateur.
   Si le mouvement est réduit, tout s'affiche directement et rien ne bouge. */
(() => {
  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pointeurFin = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const CIBLES = [
    '.collection__title',
    '.collection-list-wrapper .title-wrapper-with-link',
    '.collection-hero__title-wrapper',
    '.collection-hero__title',
    '.collection-list .card-wrapper',
    '.card-wrapper',
    '.anam-apparait',
    '[data-anam-mots]',
    '.anam-manifeste__visuels',
    '.anam-bento__grille',
  ].join(',');

  /* ---------- Découpage des titres en mots ---------- */
  const decouper = (titre) => {
    if (titre.dataset.anamDecoupe) return;
    titre.dataset.anamDecoupe = '1';
    const texte = titre.textContent.trim();
    const mots = texte.split(/\s+/);
    titre.setAttribute('aria-label', texte);
    titre.textContent = '';
    mots.forEach((mot, i) => {
      const enveloppe = document.createElement('span');
      enveloppe.className = 'anam-mot';
      enveloppe.setAttribute('aria-hidden', 'true');
      const interieur = document.createElement('span');
      interieur.style.setProperty('--i', i);
      interieur.textContent = mot;
      enveloppe.appendChild(interieur);
      titre.appendChild(enveloppe);
      if (i < mots.length - 1) titre.appendChild(document.createTextNode(' '));
    });
  };

  /* ---------- Réassurance : rang pour la cascade ---------- */
  const preparer = (racine) => {
    racine.querySelectorAll('.anam-reassure__item').forEach((el, i) => {
      if (el.classList.contains('anam-apparait')) return;
      el.classList.add('anam-apparait');
      el.style.setProperty('--anam-rang', i % 4);
    });
    if (!reduit) racine.querySelectorAll('[data-anam-mots]').forEach(decouper);
  };

  /* ---------- Apparition au défilement ---------- */
  const marquer = (el) => el.classList.add('anam-vu');
  const observateur = reduit || !('IntersectionObserver' in window)
    ? null
    : new IntersectionObserver((entrees) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        marquer(e.target);
        observateur.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  const observer = (racine = document) => {
    preparer(racine);
    racine.querySelectorAll(CIBLES).forEach((el) => {
      if (el.classList.contains('anam-vu')) return;
      if (observateur) observateur.observe(el); else marquer(el);
    });
  };

  /* ---------- Parallaxe + progression (un seul passage par image) ---------- */
  const barre = document.createElement('div');
  barre.className = 'anam-progression';
  barre.setAttribute('aria-hidden', 'true');
  document.body.appendChild(barre);

  const parallaxer = () => {
    const h = window.innerHeight;
    document.querySelectorAll('[data-anam-parallaxe]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > h) return;
      const ecart = (r.top + r.height / 2 - h / 2) / h; // -1 … 1
      const amplitude = parseFloat(el.dataset.anamParallaxe) || 0;
      const media = el.querySelector('img, svg');
      if (media) media.style.setProperty('--anam-decalage', `${(ecart * amplitude).toFixed(2)}%`);
    });
  };
  let attente = false;
  const rafraichir = () => {
    attente = false;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    barre.style.setProperty('--anam-progression', max > 0 ? Math.min(1, window.scrollY / max) : 0);
    if (!reduit) parallaxer();
  };
  const demander = () => {
    if (attente) return;
    attente = true;
    requestAnimationFrame(rafraichir);
  };
  window.addEventListener('scroll', demander, { passive: true });
  window.addEventListener('resize', demander, { passive: true });

  /* ---------- Curseur « Voir » (souris uniquement) ---------- */
  if (pointeurFin && !reduit) {
    const curseur = document.createElement('div');
    curseur.className = 'anam-curseur';
    curseur.setAttribute('aria-hidden', 'true');
    curseur.textContent = 'Voir';
    document.body.appendChild(curseur);
    let cible = { x: -100, y: -100 };
    let pos = { x: -100, y: -100 };
    const suivre = () => {
      pos = { x: pos.x + (cible.x - pos.x) * 0.22, y: pos.y + (cible.y - pos.y) * 0.22 };
      curseur.style.setProperty('--x', `${pos.x}px`);
      curseur.style.setProperty('--y', `${pos.y}px`);
      requestAnimationFrame(suivre);
    };
    requestAnimationFrame(suivre);
    document.addEventListener('pointermove', (e) => {
      cible = { x: e.clientX, y: e.clientY };
      curseur.classList.add('est-actif');
      const t = e.target instanceof Element ? e.target : null;
      const surBijou = !!t?.closest('.card-wrapper .card__media, .anam-bento__lien');
      curseur.classList.toggle('sur-bijou', surBijou);
      curseur.classList.toggle('sur-lien', !surBijou && !!t?.closest('a, button, input, select, label'));
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => curseur.classList.remove('est-actif'));
  }

  observer();
  rafraichir();
  document.addEventListener('shopify:section:load', (e) => observer(e.target));
  new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) observer(n); }));
  }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
})();
